import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

const FRANJAS = [
  { id: '8-10', label: '8-10hs', start: 8, end: 10 },
  { id: '10-12', label: '10-12hs', start: 10, end: 12 },
  { id: '12-14', label: '12-14hs', start: 12, end: 14 },
  { id: '14-16', label: '14-16hs', start: 14, end: 16 },
  { id: '16-18', label: '16-18hs', start: 16, end: 18 },
  { id: '18-20', label: '18-20hs', start: 18, end: 20 },
]

const COLORES_CATEGORIAS = ['var(--gold)', '#6db8a0', '#8b8fd4', '#e8a04c', '#e05555']

function formatNum(n) {
  return new Intl.NumberFormat('es-AR').format(n)
}

function getFranjaHoraria(timestamp) {
  const h = new Date(timestamp).getHours()
  return FRANJAS.find(f => h >= f.start && h < f.end)?.id || null
}

function getMesLabel(offset) {
  const d = new Date()
  d.setMonth(d.getMonth() - offset)
  return d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
}

function getRangoDeFechas(periodo) {
  const hoy = new Date()
  let desde, hasta

  switch (periodo) {
    case 'mes_actual':
      desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999)
      break
    case 'mes_pasado':
      desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0, 23, 59, 59, 999)
      break
    case 'ultimos_3':
      desde = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999)
      break
    default:
      desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999)
  }

  return { desde: desde.toISOString(), hasta: hasta.toISOString() }
}

export default function Dashboard() {
  const { usuario } = useAuth()
  const isAdmin = usuario?.rol === 'admin'
  const [edificios, setEdificios] = useState([])
  const [edificioSeleccionado, setEdificioSeleccionado] = useState(null)
  const [periodo, setPeriodo] = useState('mes_actual')

  const [stats, setStats] = useState({
    visitas: 0,
    paquetesRecibidos: 0,
    paquetesRetirados: 0,
    reclamosAbiertos: 0,
    reclamosCerrados: 0,
    novedadesActivas: 0,
    reservasSalas: 0,
  })

  const [visitasPorDia, setVisitasPorDia] = useState([])
  const [visitasPorFranja, setVisitasPorFranja] = useState([])
  const [topOficinas, setTopOficinas] = useState([])
  const [loading, setLoading] = useState(true)

  const edificioActivoId = isAdmin ? edificioSeleccionado : (usuario?.edificios?.id || null)

  // Cargar edificios (solo admin)
  useEffect(() => {
    if (isAdmin) {
      supabase.from('edificios').select('id, nombre').order('nombre').then(({ data }) => {
        setEdificios(data || [])
        if (data && data.length && !edificioSeleccionado) {
          setEdificioSeleccionado(data[0].id)
        }
      })
    }
  }, [isAdmin, edificioSeleccionado])

  // Cargar stats
  useEffect(() => {
    if (!edificioActivoId) return
    cargarStats()
  }, [edificioActivoId, periodo])

  const cargarStats = async () => {
    if (!edificioActivoId) return
    setLoading(true)

    const { desde, hasta } = getRangoDeFechas(periodo)

    // Supabase devuelve como máximo 1000 filas por consulta:
    // los totales se piden con count (sin traer filas) y las visitas se traen paginadas.
    const contar = (tabla, campoFecha, extra = q => q) =>
      extra(supabase.from(tabla).select('id', { count: 'exact', head: true })
        .eq('edificio_id', edificioActivoId)
        .gte(campoFecha, desde).lte(campoFecha, hasta))
        .then(({ count }) => count || 0)

    const traerAccesos = async () => {
      const PAGINA = 1000
      let todos = []
      for (let desdeFila = 0; ; desdeFila += PAGINA) {
        const { data } = await supabase.from('accesos')
          .select('timestamp, oficina_id')
          .eq('edificio_id', edificioActivoId)
          .gte('timestamp', desde).lte('timestamp', hasta)
          .order('timestamp')
          .range(desdeFila, desdeFila + PAGINA - 1)
        todos = todos.concat(data || [])
        if (!data || data.length < PAGINA) break
      }
      return todos
    }

    // Reservas de salas no tienen edificio_id: se filtran por las oficinas del edificio
    const { data: oficinasEdificio } = await supabase.from('oficinas').select('id').eq('edificio_id', edificioActivoId)
    const idsOficinas = (oficinasEdificio || []).map(o => o.id)
    const contarReservas = idsOficinas.length === 0 ? Promise.resolve(0) :
      supabase.from('reservas_salas').select('id', { count: 'exact', head: true })
        .in('oficina_id', idsOficinas).eq('estado', 'confirmada')
        .gte('created_at', desde).lte('created_at', hasta)
        .then(({ count }) => count || 0)

    const [
      dataAccesos, paquetesRecibidos, paquetesRetirados,
      reclamosAbiertos, reclamosCerrados, novedadesActivas, reservasSalas,
    ] = await Promise.all([
      traerAccesos(),
      contar('paquetes', 'created_at'),
      contar('paquetes', 'created_at', q => q.eq('estado', 'retirado')),
      contar('reclamos', 'created_at', q => q.neq('estado', 'resuelto')),
      contar('reclamos', 'created_at', q => q.eq('estado', 'resuelto')),
      // Novedades activas hoy (no depende del período, igual que el contador del menú)
      supabase.from('novedades').select('id', { count: 'exact', head: true })
        .eq('edificio_id', edificioActivoId).eq('estado', 'activa')
        .then(({ count }) => count || 0),
      contarReservas,
    ])

    setStats({
      visitas: dataAccesos.length,
      paquetesRecibidos,
      paquetesRetirados,
      reclamosAbiertos,
      reclamosCerrados,
      novedadesActivas,
      reservasSalas,
    })

    // Visitas por día
    const diasMap = {}
    dataAccesos.forEach(a => {
      const dia = a.timestamp.split('T')[0]
      diasMap[dia] = (diasMap[dia] || 0) + 1
    })
    const diasData = Object.keys(diasMap).sort().map(dia => ({
      dia: new Date(dia).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' }),
      visitas: diasMap[dia],
    }))
    setVisitasPorDia(diasData)

    // Visitas por franja horaria
    const franjasMap = {}
    FRANJAS.forEach(f => { franjasMap[f.id] = 0 })
    dataAccesos.forEach(a => {
      const franja = getFranjaHoraria(a.timestamp)
      if (franja) franjasMap[franja]++
    })
    const franjasData = FRANJAS.map(f => ({
      franja: f.label,
      visitas: franjasMap[f.id],
    }))
    setVisitasPorFranja(franjasData)

    // Top 5 oficinas con más visitas
    const ofiMap = {}
    dataAccesos.forEach(a => {
      ofiMap[a.oficina_id] = (ofiMap[a.oficina_id] || 0) + 1
    })
    const topIds = Object.entries(ofiMap).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([id]) => id)
    if (topIds.length > 0) {
      const { data: oficinasData } = await supabase.from('oficinas').select('id, numero, titular').in('id', topIds)
      const topData = topIds.map(id => {
        const ofi = (oficinasData || []).find(o => o.id === id)
        return {
          nombre: ofi ? `${ofi.numero} — ${ofi.titular}` : id,
          visitas: ofiMap[id],
        }
      })
      setTopOficinas(topData)
    } else {
      setTopOficinas([])
    }

    setLoading(false)
  }

  const periodoLabel = () => {
    if (periodo === 'mes_actual') return getMesLabel(0)
    if (periodo === 'mes_pasado') return getMesLabel(1)
    if (periodo === 'ultimos_3') return 'Últimos 3 meses'
    return ''
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: 0, fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>Dashboard</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>
          {isAdmin ? 'Métricas del sistema' : usuario?.edificios?.nombre}
        </p>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {isAdmin && edificios.length > 0 && (
          <select
            className="input input-sm"
            value={edificioSeleccionado || ''}
            onChange={e => setEdificioSeleccionado(e.target.value)}
            style={{ minWidth: 200 }}
          >
            {edificios.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        )}

        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'mes_actual', label: 'Mes actual' },
            { id: 'mes_pasado', label: 'Mes pasado' },
            { id: 'ultimos_3', label: 'Últimos 3 meses' },
          ].map(p => (
            <button key={p.id}
              className={`btn btn-sm ${periodo === p.id ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setPeriodo(p.id)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Cargando datos...</div>
      ) : (
        <>
          {/* Stats cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginBottom: 32,
          }}>
            <StatCard label="Visitas" value={stats.visitas} icon="👥" color="var(--gold)" />
            <StatCard label="Paquetes recibidos" value={stats.paquetesRecibidos} icon="📦" color="#6db8a0" />
            <StatCard label="Paquetes retirados" value={stats.paquetesRetirados} icon="✓" color="#6db8a0" />
            <StatCard label="Reclamos abiertos" value={stats.reclamosAbiertos} icon="⚠️" color="#e8a04c" />
            <StatCard label="Reclamos cerrados" value={stats.reclamosCerrados} icon="✓" color="var(--green)" />
            <StatCard label="Novedades activas" value={stats.novedadesActivas} icon="📝" color="#8b8fd4" />
            {stats.reservasSalas > 0 && <StatCard label="Reservas de salas" value={stats.reservasSalas} icon="📅" color="var(--gold)" />}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 24, fontStyle: 'italic' }}>
            Período: {periodoLabel()}
          </div>

          {/* Gráficos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>

            {/* Visitas por día */}
            <div style={{ background: 'var(--card)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 15, color: 'var(--text1)', fontWeight: 600 }}>
                👥 Visitas por día
              </h3>
              {visitasPorDia.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={visitasPorDia}>
                    <XAxis dataKey="dia" stroke="var(--text3)" style={{ fontSize: 11 }} />
                    <YAxis stroke="var(--text3)" style={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8 }}
                      labelStyle={{ color: 'var(--text1)' }}
                    />
                    <Bar dataKey="visitas" fill="var(--gold)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Visitas por franja horaria */}
            <div style={{ background: 'var(--card)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 15, color: 'var(--text1)', fontWeight: 600 }}>
                🕐 Visitas por franja horaria
              </h3>
              {visitasPorFranja.every(f => f.visitas === 0) ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={visitasPorFranja}>
                    <XAxis dataKey="franja" stroke="var(--text3)" style={{ fontSize: 11 }} />
                    <YAxis stroke="var(--text3)" style={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8 }}
                      labelStyle={{ color: 'var(--text1)' }}
                    />
                    <Bar dataKey="visitas" fill="#6db8a0" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Top oficinas */}
            <div style={{ background: 'var(--card)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 15, color: 'var(--text1)', fontWeight: 600 }}>
                🏆 Top 5 oficinas con más visitas
              </h3>
              {topOficinas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin datos</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {topOficinas.map((ofi, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{
                          fontSize: 14, fontWeight: 700, color: idx === 0 ? 'var(--gold)' : 'var(--text3)',
                          minWidth: 24,
                        }}>
                          #{idx + 1}
                        </span>
                        <span style={{ fontSize: 14, color: 'var(--text1)' }}>{ofi.nombre}</span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--gold)' }}>
                        {formatNum(ofi.visitas)} visitas
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </>
      )}
    </div>
  )
}

function StatCard({ label, value, icon, color }) {
  return (
    <div style={{
      background: 'var(--card)',
      borderRadius: 10,
      padding: '16px 18px',
      border: '1px solid var(--border)',
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: 'DM Mono, monospace' }}>
        {formatNum(value)}
      </div>
    </div>
  )
}
